import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import apiClient, { formatApiError } from "@/api/client";
import { Plus, Pencil, Trash2, X, Search, ExternalLink, Building2, Mail, Phone, Download } from "lucide-react";
import { useAuth } from "@/contexts/AuthContext";

const EMPTY = { name: "", company: "", email: "", phone: "", address: "", tax_id: "", tax_office: "", industry: "", notes: "", portal_enabled: false, portal_password: "" };

export default function CustomersPage() {
  const [items, setItems] = useState([]);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(EMPTY);
  const [q, setQ] = useState("");
  const [err, setErr] = useState("");
  const [perms, setPerms] = useState({});
  const nav = useNavigate();
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";

  useEffect(() => { apiClient.get("/me/permissions").then(r=>setPerms(r.data||{})).catch(()=>{}); }, []);
  const canExport = isAdmin || perms.members_can_export;

  const downloadCSV = async () => {
    const r = await apiClient.get("/export/customers.csv", { responseType: "blob" });
    const blob = new Blob([r.data], { type: "text/csv" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a"); a.href = url; a.download = "musteriler.csv"; a.click();
    URL.revokeObjectURL(url);
  };

  const load = (qs = q) => apiClient.get(`/customers${qs ? `?q=${encodeURIComponent(qs)}` : ""}`).then((r) => setItems(r.data));
  useEffect(() => { load(); }, []);
  useEffect(() => { const id = setTimeout(() => load(q), 250); return () => clearTimeout(id); }, [q]);

  const openCreate = () => { setEditing(null); setForm(EMPTY); setErr(""); setOpen(true); };
  const openEdit = (c, ev) => { ev?.stopPropagation(); setEditing(c); setForm({ ...EMPTY, ...c, portal_password: "" }); setErr(""); setOpen(true); };

  const save = async (e) => {
    e.preventDefault(); setErr("");
    try {
      const payload = { ...form, email: form.email || null };
      if (!payload.portal_password) delete payload.portal_password;
      if (editing) await apiClient.put(`/customers/${editing.id}`, payload);
      else await apiClient.post("/customers", payload);
      setOpen(false); load();
    } catch (er) { setErr(formatApiError(er)); }
  };

  const del = async (id, ev) => {
    ev.stopPropagation();
    if (!window.confirm("Silmek istediğinizden emin misiniz?")) return;
    await apiClient.delete(`/customers/${id}`); load();
  };

  return (
    <div className="space-y-5" data-testid="customers-page">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div>
          <h1 className="text-2xl font-bold">Müşteriler</h1>
          <p className="text-sm text-[#64748B] mt-1">{items.length} kayıt</p>
        </div>
        <div className="flex gap-2">
          <div className="relative">
            <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#94A3B8]" />
            <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Ara…" className="ky-input pl-9 w-64" data-testid="customer-search" />
          </div>
          <button onClick={openCreate} className="ky-btn ky-btn-accent" data-testid="add-customer-btn"><Plus size={14} /> Yeni Müşteri</button>
          {canExport && <button onClick={downloadCSV} className="ky-btn ky-btn-outline" data-testid="export-customers-csv"><Download size={13}/>CSV</button>}
        </div>
      </div>

      <div className="ky-card overflow-hidden">
        <table className="w-full text-sm" data-testid="customers-table">
          <thead className="bg-[#FAFAFB] border-b border-[#ECECEF]">
            <tr>
              <th className="text-left text-[11px] uppercase tracking-wider text-[#64748B] font-semibold px-5 py-3">Müşteri</th>
              <th className="text-left text-[11px] uppercase tracking-wider text-[#64748B] font-semibold px-5 py-3">İletişim</th>
              <th className="text-left text-[11px] uppercase tracking-wider text-[#64748B] font-semibold px-5 py-3">Sektör</th>
              <th className="text-left text-[11px] uppercase tracking-wider text-[#64748B] font-semibold px-5 py-3">VKN</th>
              <th className="text-left text-[11px] uppercase tracking-wider text-[#64748B] font-semibold px-5 py-3">Portal</th>
              <th className="text-right text-[11px] uppercase tracking-wider text-[#64748B] font-semibold px-5 py-3">İşlem</th>
            </tr>
          </thead>
          <tbody>
            {items.length === 0 && <tr><td colSpan={6} className="text-center py-16 text-[#94A3B8]">Kayıt yok.</td></tr>}
            {items.map((c) => (
              <tr key={c.id} onClick={() => nav(`/musteriler/${c.id}`)} className="border-b border-[#ECECEF] cursor-pointer ky-row" data-testid={`customer-row-${c.id}`}>
                <td className="px-5 py-4">
                  <div className="font-medium hover:text-[#4F46E5]">{c.name}</div>
                  <div className="text-xs text-[#64748B] flex items-center gap-1 mt-0.5"><Building2 size={10} /> {c.company || "—"}</div>
                </td>
                <td className="px-5 py-4 text-xs">
                  {c.email && <div className="flex items-center gap-1 text-[#0F172A]"><Mail size={10} className="text-[#94A3B8]" /> {c.email}</div>}
                  {c.phone && <div className="flex items-center gap-1 text-[#64748B] mt-0.5"><Phone size={10} /> {c.phone}</div>}
                </td>
                <td className="px-5 py-4 text-xs text-[#64748B]">{c.industry || "—"}</td>
                <td className="px-5 py-4 text-xs font-mono">{c.tax_id || "—"}</td>
                <td className="px-5 py-4">
                  {c.portal_enabled ? <span className="ky-badge ky-badge-success">Aktif</span> : <span className="ky-badge ky-badge-neutral">Kapalı</span>}
                </td>
                <td className="px-5 py-4 text-right">
                  <div className="inline-flex gap-1">
                    <button onClick={(e) => openEdit(c, e)} className="ky-btn ky-btn-ghost ky-btn-icon" data-testid={`edit-customer-${c.id}`}><Pencil size={13} /></button>
                    <button onClick={(e) => del(c.id, e)} className="ky-btn ky-btn-ghost ky-btn-icon text-[#EF4444]" data-testid={`delete-customer-${c.id}`}><Trash2 size={13} /></button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {open && (
        <div className="fixed inset-0 z-50 flex" onClick={() => setOpen(false)}>
          <div className="flex-1 bg-black/40" />
          <form onSubmit={save} onClick={(e) => e.stopPropagation()} className="w-full sm:w-[560px] bg-white flex flex-col ky-slide-in" data-testid="customer-form">
            <div className="px-5 py-4 border-b border-[#ECECEF] flex items-center justify-between">
              <h3 className="font-semibold">{editing ? "Müşteri Düzenle" : "Yeni Müşteri"}</h3>
              <button type="button" onClick={() => setOpen(false)} className="ky-btn ky-btn-ghost ky-btn-icon"><X size={14} /></button>
            </div>
            <div className="flex-1 overflow-y-auto p-5 space-y-4">
              {err && <div className="text-sm px-3 py-2 rounded-md bg-[#FEF2F2] text-[#DC2626] border border-[#FECACA]">{err}</div>}
              <div className="grid grid-cols-2 gap-3">
                <div className="col-span-2"><label className="block text-xs font-medium text-[#64748B] mb-1.5">Ad Soyad *</label><input required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="ky-input" data-testid="customer-input-name" /></div>
                <div><label className="block text-xs font-medium text-[#64748B] mb-1.5">Firma</label><input value={form.company || ""} onChange={(e) => setForm({ ...form, company: e.target.value })} className="ky-input" /></div>
                <div><label className="block text-xs font-medium text-[#64748B] mb-1.5">Sektör</label><input value={form.industry || ""} onChange={(e) => setForm({ ...form, industry: e.target.value })} className="ky-input" /></div>
                <div><label className="block text-xs font-medium text-[#64748B] mb-1.5">E-posta</label><input type="email" value={form.email || ""} onChange={(e) => setForm({ ...form, email: e.target.value })} className="ky-input" data-testid="customer-input-email" /></div>
                <div><label className="block text-xs font-medium text-[#64748B] mb-1.5">Telefon</label><input value={form.phone || ""} onChange={(e) => setForm({ ...form, phone: e.target.value })} className="ky-input" /></div>
                <div><label className="block text-xs font-medium text-[#64748B] mb-1.5">VKN/TCKN</label><input value={form.tax_id || ""} onChange={(e) => setForm({ ...form, tax_id: e.target.value })} className="ky-input font-mono" /></div>
                <div><label className="block text-xs font-medium text-[#64748B] mb-1.5">Vergi Dairesi</label><input value={form.tax_office || ""} onChange={(e) => setForm({ ...form, tax_office: e.target.value })} className="ky-input" /></div>
                <div className="col-span-2"><label className="block text-xs font-medium text-[#64748B] mb-1.5">Adres</label><textarea value={form.address || ""} onChange={(e) => setForm({ ...form, address: e.target.value })} className="ky-textarea" rows={2} /></div>
                <div className="col-span-2"><label className="block text-xs font-medium text-[#64748B] mb-1.5">Notlar</label><textarea value={form.notes || ""} onChange={(e) => setForm({ ...form, notes: e.target.value })} className="ky-textarea" rows={3} /></div>
              </div>
              <div className="pt-4 border-t border-[#ECECEF]">
                <label className="flex items-center gap-2 text-sm">
                  <input type="checkbox" checked={form.portal_enabled} onChange={(e) => setForm({ ...form, portal_enabled: e.target.checked })} data-testid="customer-portal-enabled" />
                  <span className="font-medium">Müşteri portalı erişimi etkin</span>
                </label>
                <p className="text-xs text-[#64748B] mt-1">Aktifse müşteri /portal sayfasından e-postasıyla giriş yapabilir.</p>
                {form.portal_enabled && (
                  <input type="password" placeholder={editing ? "Şifreyi değiştirmek için yazın (boş bırakılırsa değişmez)" : "Portal şifresi *"} value={form.portal_password} onChange={(e) => setForm({ ...form, portal_password: e.target.value })} className="ky-input mt-2" data-testid="customer-portal-password" />
                )}
              </div>
            </div>
            <div className="px-5 py-4 border-t border-[#ECECEF] flex gap-2">
              <button type="button" onClick={() => setOpen(false)} className="ky-btn ky-btn-outline flex-1">İptal</button>
              <button type="submit" className="ky-btn ky-btn-accent flex-1" data-testid="save-customer">Kaydet</button>
            </div>
          </form>
        </div>
      )}
    </div>
  );
}
